/**
 * Hook for fetching and parsing OpenAPI spec
 */

import { useState, useEffect, useCallback } from 'react'
import { getAccessToken } from '../../../services/apiService'

export interface ApiEndpoint {
  path: string
  method: string
  summary?: string
  description?: string
  operationId?: string
  requestBodyExample?: string
}

interface OpenApiSpec {
  openapi: string
  paths: Record<string, Record<string, {
    summary?: string
    description?: string
    operationId?: string
    requestBody?: {
      content?: {
        'application/json'?: {
          example?: unknown
          examples?: Record<string, { value: unknown }>
          schema?: {
            example?: unknown
            examples?: Record<string, { value: unknown }>
          }
        }
      }
    }
  }>>
}

interface UseOpenApiSpecResult {
  endpoints: ApiEndpoint[]
  isLoading: boolean
  error: string | null
  refetch: () => void
}

// Helper to convert example to JSON string without double-encoding
function exampleToJsonString(example: unknown): string {
  if (typeof example === 'string') {
    // Example is already a string - check if it's valid JSON
    try {
      JSON.parse(example)
      return example  // Already valid JSON string, use as-is
    } catch {
      // Not valid JSON, stringify it
      return JSON.stringify(example, null, 2)
    }
  }
  // Example is an object, stringify it
  return JSON.stringify(example, null, 2)
}

export function useOpenApiSpec(): UseOpenApiSpecResult {
  const [endpoints, setEndpoints] = useState<ApiEndpoint[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSpec = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const accessToken = await getAccessToken()
      const response = await fetch('/api/openapi/v3.json', {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!response.ok) {
        throw new Error(`Failed to fetch OpenAPI spec: ${response.status}`)
      }

      const spec: OpenApiSpec = await response.json()
      const parsed: ApiEndpoint[] = []

      for (const [path, methods] of Object.entries(spec.paths)) {
        for (const [method, details] of Object.entries(methods)) {
          // Skip non-HTTP methods (like parameters, servers, etc.)
          if (!['get', 'post', 'put', 'patch', 'delete', 'options', 'head'].includes(method.toLowerCase())) {
            continue
          }

          // Ensure path has /api/ prefix (Azure Functions default route prefix)
          let normalizedPath = path
          if (!path.startsWith('/api/') && !path.startsWith('/api')) {
            normalizedPath = `/api${path.startsWith('/') ? '' : '/'}${path}`
          }

          // Extract request body example - check multiple locations
          let requestBodyExample: string | undefined
          const requestBody = details.requestBody
          if (requestBody?.content?.['application/json']) {
            const jsonContent = requestBody.content['application/json']

            // Priority 1: Direct example on content
            if (jsonContent.example) {
              requestBodyExample = exampleToJsonString(jsonContent.example)
            }
            // Priority 2: Named examples on content
            else if (jsonContent.examples) {
              const firstKey = Object.keys(jsonContent.examples)[0]
              if (firstKey && jsonContent.examples[firstKey]?.value) {
                requestBodyExample = exampleToJsonString(jsonContent.examples[firstKey].value)
              }
            }
            // Priority 3: Example on schema (Azure Functions OpenAPI extension often uses this)
            else if (jsonContent.schema?.example) {
              requestBodyExample = exampleToJsonString(jsonContent.schema.example)
            }
            // Priority 4: Named examples on schema
            else if (jsonContent.schema?.examples) {
              const firstKey = Object.keys(jsonContent.schema.examples)[0]
              if (firstKey && jsonContent.schema.examples[firstKey]?.value) {
                requestBodyExample = exampleToJsonString(jsonContent.schema.examples[firstKey].value)
              }
            }
          }

          parsed.push({
            path: normalizedPath,
            method: method.toUpperCase(),
            summary: details.summary,
            description: details.description,
            operationId: details.operationId,
            requestBodyExample,
          })
        }
      }

      // Sort by path, then by method
      parsed.sort((a, b) => {
        const pathCompare = a.path.localeCompare(b.path)
        if (pathCompare !== 0) return pathCompare
        return a.method.localeCompare(b.method)
      })

      setEndpoints(parsed)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load OpenAPI spec')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSpec()
  }, [fetchSpec])

  return {
    endpoints,
    isLoading,
    error,
    refetch: fetchSpec,
  }
}
